import { useMemo, type ReactNode } from 'react';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { AuthNotice } from '@l2u/ui/components/AuthSplitLayout.tsx';
import { getSupabase } from '@l2u/ui/lib/supabase.ts';
import { BRAND } from '@l2u/ui/lib/brand.ts';
import { useMfa } from '@l2u/ui/lib/useMfa.ts';
import { gateDecision } from '@l2u/ui/lib/mfa.ts';
import { MfaEnrolScreen } from '@l2u/ui/auth/MfaEnrolScreen.tsx';
import { MfaStepUpScreen } from '@l2u/ui/auth/MfaStepUpScreen.tsx';
import { useCompanyAuth } from '../lib/auth/company.tsx';
import { gateView } from './vendorMfaGate.ts';

// Two-factor gate for the vendor portal. Wraps CompanyShell from App.tsx.
//
// WHY IT WRAPS THE SHELL RATHER THAN LIVING INSIDE IT (spec §3.3, D6)
//
// This gate and both screens talk ONLY to Supabase — never to the Worker.
// Enrolment and step-up are supabase.auth.mfa.* calls, so a vendor who has not
// yet enrolled makes no /api/me request at all. That is what lets companyAuth
// demand aal2 UNCONDITIONALLY, with no "unless they have no factors yet"
// carve-out. There is no exception to get wrong, and no aal1 hole for a vendor
// who simply never enrols.
//
// It has to sit ABOVE CompanyShell, not inside it: the shell's three useQuery
// hooks fire on `enabled: !!session` regardless of what the component returns,
// so an early return inside the shell would still have hit /api/me — at aal1,
// which now 403s.
//
// The cost of this arrangement is small and deliberate: the enrolment screen
// cannot pre-fill a phone number from the vendor's profile, because that would
// need /api/me. Vendors type their mobile in Phase C. contact_phone is often a
// landline anyway.
export function VendorMfaGate({ children }: { children: ReactNode }) {
  const { session, loading: authLoading, signOut } = useCompanyAuth();

  const client = useMemo(() => {
    try {
      return getSupabase();
    } catch {
      return null;
    }
  }, []);

  // Keyed on the access token (D8): a step-up, a refreshSession() after a
  // mfa_required, or an admin's Reset 2FA landing on the next rotation all
  // mint a new token, and each re-reads the factor state.
  const mfa = useMfa(client, session?.access_token ?? null);
  const view = gateView({
    authLoading,
    hasSession: !!session,
    mfaLoading: mfa.loading,
    mfaError: mfa.error,
    decision: gateDecision(mfa.state),
  });

  if (view === 'spinner') return <CenteredSpinner />;
  if (view === 'children') return <>{children}</>;

  if (view === 'error') {
    return (
      <AuthNotice
        variant="vendor"
        title="We could not check your security settings"
        error={mfa.error}
        action={
          <button
            type="button"
            onClick={() => void mfa.refresh()}
            className="font-semibold text-brand-primary hover:underline"
          >
            Try again
          </button>
        }
      >
        This is usually a connection problem. Try again in a moment.
      </AuthNotice>
    );
  }

  if (!client) return <CenteredSpinner />;

  const onSignOut = () => {
    void signOut();
  };

  if (view === 'enrol') {
    return (
      <MfaEnrolScreen
        client={client}
        // Per-environment (D12): Prod must not offer SMS before its Send SMS
        // hook and Phone factor exist, or the code never arrives.
        smsEnabled={import.meta.env.VITE_VENDOR_MFA_SMS === 'true'}
        supportEmail={BRAND.supportEmail}
        onEnrolled={() => void mfa.refresh()}
        onSignOut={onSignOut}
      />
    );
  }

  return (
    <MfaStepUpScreen
      client={client}
      factorId={mfa.state.verifiedFactorId ?? ''}
      factorType={mfa.state.factorType ?? 'totp'}
      supportEmail={BRAND.supportEmail}
      onVerified={() => void mfa.refresh()}
      onSignOut={onSignOut}
    />
  );
}
